import * as React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { AntDTable } from "@/common/components/ui";
import { TableColumnsType } from "antd";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Download,
  Building2,
  TrendingUp,
  Layers,
  Globe,
  CheckCircle2,
  MoreHorizontal,
} from "lucide-react";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";
import { ScreenerItem } from "@/utils/types/Screener/Screener";
import { notify } from "@/common/components/common/Notification";
import * as XLSX from "xlsx";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import { del } from "@/services/axiosUitils";

interface Company {
  id: string;
  name: string;
  shortName: string;
  nseSymbol: string | null;
  bseCode: string | null;
  isin: string | null;
  sector: string | null;
  industry: string | null;
  marketCap: number;
  isNseListed: boolean;
  isBseListed: boolean;
  price?: number;
  changePercent?: number;
  peRatio?: number;
}

const mapScreenerItemToCompany = (item: ScreenerItem): Company => ({
  id: item.co_code?.toString() || item.id,
  name: item.companyname || "",
  shortName: item.companyshortname || "",
  nseSymbol: item.nsesymbol || null,
  bseCode: item.bsecode || null,
  isin: null, // Not available in ScreenerItem
  sector: item.sectorname || null,
  industry: item.industryname || null,
  marketCap: parseFloat(item.mcap) || 0,
  isNseListed: item.nselisted === "yes",
  isBseListed: item.bselisted === "yes",
  price: 0, // Not available in API
  changePercent: 0, // Not available in API
  peRatio: 0, // Not available in API
});

const CompanyMasterList = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]); // Store all companies for filtering
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<Company[]>([]);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Fetch all companies (you may want to implement pagination)
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch first page with large page size to get all companies
      // In production, you might want to implement proper pagination
      const response = await ScreenerService.getCompanyMaster({
        page: 1,
        page_size: 1000, // Adjust based on your needs
      });

      if (response?.success && response?.data) {
        const mappedCompanies = response.data.results.map(
          mapScreenerItemToCompany
        );
        setAllCompanies(mappedCompanies);
        setCompanies(mappedCompanies);
      } else {
        setCompanies([]);
        setAllCompanies([]);
        notify.error("Failed to fetch company data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      notify.error("Failed to fetch company data");
      setCompanies([]);
      setAllCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Extract unique sectors from companies
  const sectors = useMemo(() => {
    const uniqueSectors = Array.from(
      new Set(
        allCompanies
          .map(c => c.sector)
          .filter((s): s is string => s !== null && s !== "")
      )
    ).sort();
    return uniqueSectors;
  }, [allCompanies]);

  // Filter companies
  const filteredCompanies = useMemo(() => {
    return allCompanies.filter(company => {
      const matchesSearch =
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.nseSymbol?.toLowerCase().includes(searchTerm.toLowerCase()) ??
          false) ||
        (company.bseCode?.toLowerCase().includes(searchTerm.toLowerCase()) ??
          false) ||
        (company.shortName?.toLowerCase().includes(searchTerm.toLowerCase()) ??
          false);

      const matchesSector =
        sectorFilter === "all" ||
        (company.sector && company.sector === sectorFilter);

      return matchesSearch && matchesSector;
    });
  }, [allCompanies, searchTerm, sectorFilter]);

  // Update displayed companies when filters change
  useEffect(() => {
    setCompanies(filteredCompanies);
  }, [filteredCompanies]);

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: Company[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(company => ({
      ID: company.id,
      Name: company.name,
      "Short Name": company.shortName,
      "NSE Symbol": company.nseSymbol ?? "",
      "BSE Code": company.bseCode ?? "",
      Sector: company.sector ?? "",
      Industry: company.industry ?? "",
      "Market Cap (Cr)": company.marketCap,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Companies");

    const datePart = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `company_master_${datePart}.xlsx`);
    notify.success("Exported", `${rows.length} company(s) exported.`);
  };

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        // Assuming the delete endpoint follows the pattern /screener/company-master/{id}/
        await del(`/screener/company-master/${id}/`);
        setAllCompanies(allCompanies.filter(c => c.id !== id));
        notify.success(
          "Company Deleted",
          `${name} has been removed successfully.`
        );
      } catch (error) {
        console.error("Error deleting company:", error);
        notify.error("Failed to delete company");
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openDropdownId &&
        dropdownRefs.current[openDropdownId] &&
        !dropdownRefs.current[openDropdownId]?.contains(event.target as Node)
      ) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  const totalMarketCap = allCompanies.reduce(
    (sum, c) => sum + (c.marketCap || 0),
    0
  );

  const listedCount = allCompanies.filter(
    c => c.isNseListed || c.isBseListed
  ).length;

  // Table columns configuration
  const columns: TableColumnsType<Company> = useMemo(
    () => [
      {
        title: "Entity / Security Identity",
        key: "entity",
        width: 400,
        render: (_: any, company: Company) => (
          <div className="flex items-center gap-4 pl-8">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 shadow-sm transition-all group-hover:border-blue-200 group-hover:from-blue-50 group-hover:to-blue-100">
              <Building2 className="h-5 w-5 text-slate-400 group-hover:text-blue-500" />
            </div>
            <div className="space-y-0.5">
              <p className="font-sans text-sm font-black uppercase tracking-tight text-slate-800 transition-colors group-hover:text-blue-700">
                {company.name}
              </p>
              <div className="flex items-center gap-2">
                <span className="font-sans text-[10px] font-bold uppercase tracking-tight text-slate-400">
                  {company.sector ?? "Uncategorized"}
                </span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="font-sans text-[10px] font-black uppercase tracking-tight text-blue-600">
                  {company.shortName ?? company.nseSymbol ?? company.bseCode}
                </span>
              </div>
            </div>
          </div>
        ),
      },
      {
        title: "Exchange Tickers",
        key: "tickers",
        render: (_: any, company: Company) => (
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-1.5">
              {company.nseSymbol && (
                <div className="flex items-center">
                  <span className="rounded-l bg-blue-600 px-1 py-0.5 font-sans text-[8px] font-black uppercase text-white">
                    NSE
                  </span>
                  <span className="rounded-r border border-blue-600 px-1.5 py-0.5 font-sans text-[10px] font-black tracking-tighter text-blue-600">
                    {company.nseSymbol}
                  </span>
                </div>
              )}
              {company.bseCode && (
                <div className="flex items-center">
                  <span className="rounded-l bg-slate-800 px-1 py-0.5 font-sans text-[8px] font-black uppercase text-white">
                    BSE
                  </span>
                  <span className="rounded-r border border-slate-800 px-1.5 py-0.5 font-sans text-[10px] font-black tracking-tighter text-slate-800">
                    {company.bseCode}
                  </span>
                </div>
              )}
            </div>
            {company.isin && (
              <div className="flex items-center gap-2">
                <Badge className="pointer-events-none border-none bg-emerald-50 font-sans text-[8px] font-black uppercase text-emerald-600">
                  ISIN: {company.isin}
                </Badge>
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Valuation (Cr)",
        key: "valuation",
        align: "right",
        render: (_: any, company: Company) => (
          <div className="text-right">
            <p className="font-sans text-[11px] font-black tracking-tight text-slate-900">
              ₹{company.marketCap.toLocaleString()}Cr
            </p>
            <p className="font-sans text-[9px] font-bold uppercase tracking-wider text-slate-400">
              Equity Value
            </p>
          </div>
        ),
      },
      {
        title: "Real-time Price",
        key: "price",
        align: "right",
        render: (_: any, company: Company) => (
          <div className="text-right">
            <div className="font-sans text-sm font-black tracking-tighter text-slate-900">
              ₹{company.price?.toLocaleString() ?? "N/A"}
            </div>
            {company.changePercent !== undefined && (
              <div
                className={`flex items-center justify-end gap-1 font-sans text-[10px] font-black ${
                  company.changePercent >= 0
                    ? "text-emerald-500"
                    : "text-rose-500"
                }`}
              >
                {company.changePercent >= 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingUp className="h-3 w-3 rotate-180" />
                )}
                {company.changePercent > 0 ? "+" : ""}
                {company.changePercent}%
              </div>
            )}
          </div>
        ),
      },
      {
        title: "P/E",
        key: "peRatio",
        align: "right",
        render: (_: any, company: Company) => (
          <Badge
            variant="outline"
            className="border-slate-200 bg-slate-50 font-sans text-[11px] font-black text-slate-700"
          >
            {company.peRatio ?? "N/A"}
          </Badge>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "right",
        width: 200,
        fixed: "right" as const,
        render: (_: any, company: Company) => (
          <div className="flex justify-end gap-2 pr-8 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              variant="secondary"
              size="sm"
              className="h-8 gap-2 rounded-md px-4 font-sans text-[10px] font-bold uppercase"
              onClick={() =>
                navigate(
                  `${finEaseHomeNavigationRoutes.COMPANY_MASTER_LIST}/edit/${company.id}`
                )
              }
            >
              <Edit className="h-3 w-3" /> Edit
            </Button>
            <div
              className="relative"
              ref={el => (dropdownRefs.current[company.id] = el)}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-slate-200"
                onClick={() =>
                  setOpenDropdownId(
                    openDropdownId === company.id ? null : company.id
                  )
                }
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
              {openDropdownId === company.id && (
                <div className="absolute right-0 top-10 z-50 w-48 rounded-md border bg-white shadow-lg">
                  <div className="p-2">
                    <div className="px-2 py-1.5 font-sans text-[10px] font-black uppercase text-slate-400">
                      Management
                    </div>
                    <button
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-2 font-sans text-xs font-bold hover:bg-slate-50"
                      onClick={() => {
                        navigate(`/company/NSE/${company.nseSymbol}`);
                        setOpenDropdownId(null);
                      }}
                    >
                      <Globe className="h-3.5 w-3.5" /> View Public Page
                    </button>
                    <button
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-2 font-sans text-xs font-bold hover:bg-slate-50"
                      onClick={() => {
                        fetchData();
                        setOpenDropdownId(null);
                      }}
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Force Sync Data
                    </button>
                    <button
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-2 font-sans text-xs font-bold text-rose-600 hover:bg-rose-50"
                      onClick={() => {
                        handleDelete(company.id, company.name);
                        setOpenDropdownId(null);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove Company
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ],
    [openDropdownId, navigate, handleDelete, dropdownRefs]
  );

  // Empty state component
  const emptyState = (
    <div className="flex flex-col items-center justify-center space-y-3 py-16 opacity-30">
      <Search className="h-12 w-12 text-slate-300" />
      <div className="space-y-1">
        <p className="font-sans text-xs font-black uppercase tracking-widest text-slate-500">
          No Intelligence Matching Selection
        </p>
        <p className="font-sans text-[10px] font-bold uppercase text-slate-400">
          Refine filters or add a new entity to the master database
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-6 font-sans">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <Building2 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-sans text-2xl font-bold text-white">
                Company Master
              </h1>
              <p className="font-sans text-white/80">
                Manage company database, fundamentals, and market data
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw
                className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              />{" "}
              Sync
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExport}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
            <Button
              className="gap-2 bg-white text-secondary hover:bg-white/90"
              onClick={() =>
                navigate(
                  finEaseHomeNavigationRoutes.COMPANY_MASTER_LIST + "/add"
                )
              }
            >
              <Plus className="h-4 w-4" /> Add Company
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Summary Tiles */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[
          {
            label: "Total Entities",
            value: allCompanies.length,
            sub: "Indexed in Master",
            icon: Building2,
            color: "text-blue-600",
            bg: "bg-blue-50",
          },
          {
            label: "Active Sectors",
            value: sectors.length,
            sub: "Industry Groups",
            icon: Layers,
            color: "text-emerald-600",
            bg: "bg-emerald-50",
          },
          {
            label: "Market Dominance",
            value: `₹${(totalMarketCap / 1000).toFixed(1)}T`,
            sub: "Total Market Cap",
            icon: TrendingUp,
            color: "text-purple-600",
            bg: "bg-purple-50",
          },
          {
            label: "Data Integrity",
            value: "98.2%",
            sub: "Form Completeness",
            icon: CheckCircle2,
            color: "text-amber-600",
            bg: "bg-amber-50",
          },
        ].map(kpi => (
          <Card
            key={kpi.label}
            className="group overflow-hidden border-slate-200 shadow-sm transition-all hover:border-blue-200"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="mb-1 font-sans text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {kpi.label}
                  </p>
                  <h3 className="font-sans text-2xl font-black text-slate-800">
                    {kpi.value}
                  </h3>
                  <p className="mt-1 font-sans text-[10px] font-bold uppercase text-slate-400">
                    {kpi.sub}
                  </p>
                </div>
                <div
                  className={`rounded-xl p-3 ${kpi.bg} ${kpi.color} transition-transform group-hover:scale-110`}
                >
                  <kpi.icon className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters & Actions Area */}
      <div className="flex flex-col items-end justify-between gap-4 lg:flex-row lg:items-center">
        <div className="flex w-full flex-1 items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Find by company, symbol or ISIN..."
              className="h-11 w-full rounded-md border border-slate-200 bg-white pl-10 font-sans text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="h-11 w-[180px] border-slate-200 bg-white font-sans text-xs font-bold uppercase tracking-tight shadow-sm">
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value="all"
                className="font-sans text-xs font-bold uppercase"
              >
                All Sectors
              </SelectItem>
              {sectors.map(sector => (
                <SelectItem
                  key={sector}
                  value={sector}
                  className="font-sans text-xs font-bold uppercase"
                >
                  {sector}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className="h-8 border-none bg-slate-100 px-3 font-sans text-[9px] font-bold uppercase text-slate-500"
          >
            Listed: {listedCount}
          </Badge>
        </div>
      </div>

      {/* Results Table */}
      <AntDTable<Company>
        showSettings
        data={companies}
        columns={columns}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: 1200 }}
        emptyText={emptyState}
        className="[&_.ant-table-tbody>tr:hover]:bg-blue-50/30 [&_.ant-table-tbody>tr]:cursor-default [&_.ant-table-tbody>tr]:transition-all [&_.ant-table-thead>tr>th]:border-b [&_.ant-table-thead>tr>th]:border-slate-100 [&_.ant-table-thead>tr>th]:bg-slate-50/50"
        onRow={() =>
          ({
            className: "group",
          }) as any
        }
      />
    </div>
  );
};

export default CompanyMasterList;
